import { useTranslation } from 'react-i18next'

import { Flex, IconButton, Text, Tooltip } from '@enechain/ecloud-designsystem'
import {
  FaArrowDownWideShortRegular,
  FaArrowUpArrowDownRegular,
  FaArrowUpShortWideRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import type { SortOrder } from '~/models/tableSort'

function toAriaSort(
  order: SortOrder | undefined,
): 'ascending' | 'descending' | 'none' {
  if (order === undefined) return 'none'
  return order === 'asc' ? 'ascending' : 'descending'
}

type Props = {
  label: string
  /** 今この列で並んでいる向き。 `undefined` はこの列で並んでいない。 */
  order: SortOrder | undefined
  /** 押した時に並ぶ向き。 画面ごとに切替周期が違うため呼び出し側が決める。 */
  nextOrder: SortOrder
  onClick: () => void
}

/**
 * ソート可能列ヘッダー (業務ワークフロー一覧 AC-WFW-001 / 需要調達計画一覧 AC-PPL-012-1)。
 *
 * `MasterDataTable` は 20+ ページで使われるため汎用 sortable 機構は足さず、 column
 * の `header` にこの部品を、 `ariaSort` に th の `aria-sort` を差し込む。 両者を同じ向きから
 * 作るため、 caller は `sortableColumn` の戻り値を column に spread する。
 *
 * **[DS差異]** 押せるのは右のソート標だけで、 文字は押せない
 * (`design/ui-feasibility/components/table.md`「2026-10-13 ユーザー指定 (React)」。
 * 8080 は th 全体を押せる)。 標の絵柄と色は 8080 の `.sort-arrow` / `.sort-active` と
 * 1:1 (非アクティブ = 双方向矢印 + mid-emphasis、 アクティブ = 向き付き + high-emphasis
 * + `Surface.secondary`)。 器は DS IconButton の xs で、 8080 の `.sort-arrow`
 * (padding 4px + 14px の標) に最も近い DS の寸法。
 *
 * aria-label は列名付き (読み上げで列が分かるように)、 Tooltip は 8080 の `data-tooltip`
 * どおり向きだけを出す。
 */
const SortableColumnHeader = ({
  label,
  order,
  nextOrder,
  onClick,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const nextOrderLabel = t(
    nextOrder === 'asc'
      ? 'common:tableSort.ascending'
      : 'common:tableSort.descending',
  )
  const active = order !== undefined
  return (
    <Flex as="span" alignItems="center" gap={1} display="inline-flex">
      <Text
        as="span"
        textStyle="tableHeader"
        color="inherit"
        fontWeight="inherit"
      >
        {label}
      </Text>
      <Tooltip content={nextOrderLabel}>
        <IconButton
          color={active ? 'Text.highEmphasis' : 'Text.midEmphasis'}
          aria-label={`${label} / ${nextOrderLabel}`}
          backgroundColor={active ? 'Surface.secondary' : undefined}
          onClick={onClick}
          size="xs"
          variant="ghost"
        >
          {order === 'asc' ? (
            <FaArrowUpShortWideRegular boxSize={iconSize.sortIndicator} />
          ) : order === 'desc' ? (
            <FaArrowDownWideShortRegular boxSize={iconSize.sortIndicator} />
          ) : (
            <FaArrowUpArrowDownRegular boxSize={iconSize.sortIndicator} />
          )}
        </IconButton>
      </Tooltip>
    </Flex>
  )
}

/**
 * ソート可能な列の `header` と `ariaSort` を同じ向きから作る。
 * `MasterDataTableColumn` に spread して使う。
 */
export function sortableColumn(props: Props): {
  header: React.ReactElement
  ariaSort: 'ascending' | 'descending' | 'none'
} {
  return {
    header: <SortableColumnHeader {...props} />,
    ariaSort: toAriaSort(props.order),
  }
}
